import store from "@/store";
import i18n from "@/i18n";
import { loadScript } from "@/utils/loadScript.js";
import { Api } from "@/services/api.js";
import { resetFlutterSyncState, syncFlutterLogin } from "@/utils/flutterAuthBridge.js";

const api = new Api();

// Paths that must stay reachable without an SSO session (public landing pages).
// NOTE: the route `meta.requiresAuth` flag is not honoured by the guard below,
// so genuinely public pages have to be listed here explicitly.
const PUBLIC_PATHS = ["/download"];

function updateLanguage(setting, lang) {
  if (lang) {
    let language = lang == "en" ? lang : "zh-TW";
    localStorage.setItem("lang", language);
    setting.locale = language;
  } else {
    setting.locale = localStorage.getItem("lang");
  }
}

async function getUserInfo(userId) {
  const apiHost = process.env.VUE_APP_API_HOST;
  const merchantId = process.env.VUE_APP_MERCHANT_ID;
  const config = {
    method: "get",
    url: `${apiHost}/identities/v1/merchants/${merchantId}/members/${userId}`,
    headers: {},
  };

  const res = await api.$http(config);
  return api.useData(res);
}

async function sdk() {
  try {
    await loadScript(process.env.VUE_APP_SSO_SDK);

    store.commit("updateQcssoSdk", qcsso);

    let qcssoConfig = { appId: process.env.VUE_APP_ID };
    
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const referralCode = urlParams.get('referralCode');
    if (referralCode) qcssoConfig.params = { referralCode };
    
    await store.state.qcssoSdk.init(qcssoConfig);

    if (! store.state.qcssoSdk.isLoggedIn()) {
      resetFlutterSyncState();
      store.commit("updateIsLoggedIn", false);
      store.commit("updateUser", {});
      store.commit("updateUserInfo", {});
    } else {
      store.commit("updateIsLoggedIn", true);

      const user = await store.state.qcssoSdk.getProfile();
      store.commit("updateUser", user);

      const profile = await getUserInfo(user.userId);
      store.commit("updateUserInfo", profile);
      syncFlutterLogin({ userId: user.userId, memberId: profile.memberId, force: true });
    }

    return true;
  } catch (err) {
    console.error(err);
  }
}

async function auth(router) {
  // Init qcsso sdk.
  await sdk();

  router.beforeEach((to, from, next) => {
    store.commit("updateCurrentPath", to.path);
    updateLanguage(i18n, to.query.lang);

    // Public pages stay reachable without forcing an SSO login redirect.
    if (PUBLIC_PATHS.includes(to.path)) return next();

    // Check session live?
    // If not reset store state, ui will show non-login mode.
    if (! store.state.qcssoSdk.isLoggedIn())
    {
      login();
      return;
    }

    if (to.path == "/") return next("/home");
    next();
  });
}

export function login() {
  try {
    store.state.qcssoSdk.login({
      redirectUrl: `${process.env.VUE_APP_HOST}${process.env.BASE_URL}`,
    });
  } catch (err) {
    console.error(err);
  }
}

export async function logout() {
  try {
    await store.state.qcssoSdk.logout({
      logoutRedirectUri: `${process.env.VUE_APP_HOST}${process.env.BASE_URL}`,
    });
  } catch (err) {
    console.error(err);
  }
}

export default auth;
